
import { Formik } from 'formik';
import { object, ObjectSchema, string } from 'yup';

import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { createNotInAreaUser } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useStates } from '@uplift/queries';

import {
  StepContainer, StepContent, StepDescription, StepForm, StepHeading,
  StepNavigator, StepNextButton
} from '../../components';
import { StateSelectInput } from '../../default/steps';
import { NotAvailableNextData, WhereNextData } from '../types';

export type WhereProps = {
  onNext: (data: WhereNextData) => void;
  onNotAvailable: (data: NotAvailableNextData) => void;
  onBack: () => void;
  stateId: string | null;
};

type WhereFormType = {
  stateId: string;
  notAvailableStateId: string;
};

const STATE_NONE_ID = 'none';

export const Where: React.VFC<WhereProps> = props => {
  const currentUser = useCurrentuser();
  const { availableStates } = useStates();

  const onSubmit = async (values: WhereFormType) => {
    if (values.stateId === STATE_NONE_ID) {
      await createNotInAreaUser(values.notAvailableStateId, currentUser.email);

      return props.onNotAvailable({ stateId: values.notAvailableStateId });
    }

    return props.onNext({ stateId: values.stateId });
  };

  return (
    <Formik
      initialValues={{
        stateId: props.stateId || '',
        notAvailableStateId: '',
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnMount
    >
      {formik => {
        const isNoneSelected = formik.values.stateId === STATE_NONE_ID;

        return (
          <StepContainer>
            <StepContent>
              <StepHeading>Where will you be joining virtual sessions from?</StepHeading>
              <StepDescription>We need your location to match you with providers licensed in your area.</StepDescription>
              <StepForm onSubmit={formik.handleSubmit} maxWidth="384px">
                <StateSelectInput
                  name="stateId"
                  states={availableStates.concat({ fullName: 'None of these', shortName: '', id: STATE_NONE_ID })}
                  data-testid="state-select-input"
                />
                {isNoneSelected && <NotInState />}
              </StepForm>
            </StepContent>
            <StepNavigator onBack={props.onBack}>
              {isNoneSelected ? <StepNextButton endIcon={null} data-testid="notify-me-btn">Notify Me</StepNextButton> : <StepNextButton disabled={!formik.isValid || formik.isSubmitting} />}
            </StepNavigator>
          </StepContainer>
        );
      }}
    </Formik>
  );
};


const NotInState: React.VFC = () => {
  const { states } = useStates();
  const { classes } = useNotInStateStyles();

  const notAvailableStates = states.filter(state => !state.available);

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>We’re working to expand to additional locations</Typography>
      <Typography>
        To ensure timely access to care, we suggest contacting your school to get a referral for a new provider licensed in your area.
        <br /><br />
        Sign up to be notified when we’re available in your state.
      </Typography>
      <div className={classes.form}>
        <StateSelectInput
          states={notAvailableStates}
          label="Select a state"
          name="notAvailableStateId"
        />
      </div>
    </div>
  );
};

const useNotInStateStyles = makeStyles({ name: 'NotInState' })(theme => ({
  root: {
    marginTop: theme.spacing(2),
    background: theme.palette.grey[100],
    padding: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  form: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  input: {
    height: '56px'
  }
}));

const validationSchema = (): ObjectSchema<WhereFormType> => object({
  stateId: string().nullable().required('Please select your state'),
  notAvailableStateId: string().when('stateId', {
    is: STATE_NONE_ID,
    then: s => s.nullable().required('Please select a state')
  })
});
